import { type ReactElement } from 'react';
import { createReactTreeMounter } from '@direct-frontend/stdlib';

/**
 * Функция, которая отрендерит временный компонент.
 * Принимает функцию unmount, которая размаунтит компонент при вызове
 * @param unmount Функция, которая удаляет инстанс компонента
 * @returns Содержимое, которое нужно отрендерить отдельно от основного приложения.
 */
type Render = (unmount: Unmount) => ReactElement;

/**
 * Функция, которая удаляет инстанс компонента
 */
type Unmount = () => void;

const mounter = createReactTreeMounter();

/**
 * Компонент, в который замаунтятся компоненты, отрисованные через renderTemporaryComponent.
 * Должен находиться в корне приложения, но ниже глобальных провайдеров контекстов,
 * чтобы временные компоненты получили доступ к ним
 * @kind component
 */
export const TemporaryMountPoint = mounter.MountPoint;

/**
 * Метод позволяет отрендерить временный компонент в реакт дерево.
 * Компонент будет отрендерен в TemporaryMountPoint.
 *
 * @kind helper
 * @param render Функция, которая отрендерит временный компонент
 * @returns Функция для размонтирования компонента
 *
 */
export function renderTemporaryComponent(render: Render) {
    let id: number | null = null;

    function unmount() {
        if (id === null) {
            return;
        }

        mounter.unmount(id);
    }

    id = mounter.mount(render(unmount));

    return unmount;
}
